import * as Logger from './logger.js'
import * as Utils from './utils.js'
import { HSGrid } from './grid.js';
import { UserSession } from './session.js';
import { hasCapability } from './capabilities.js';
import styles from './gridstack.css';
import template from './gridstack.html';
import gridTemplate from './grid.html';
import cardTemplate from './card.html';
import { GridStack } from 'gridstack';
import { HSComponent } from './component.js';
import { HSCard } from './card.js';
import { HSChart } from './chart.js';
import { HSMetric } from './metric.js';
import { HSStatsTable } from './stats-table.js';
import { HSTable } from './table.js';
import { HSTiles } from './tiles.js';
import { HSLinkCard } from './link-card.js';
import { HSViewPicker } from './view-picker.js';
import { HSTimePicker } from './time-picker.js';
import { HSSnapshotPicker } from './snapshot-picker.js';
import { HSStatsColumnPicker } from './stats-column-picker.js';
import { HSDateRangePicker } from './date-range-picker.js';
import { HSEditor } from './editor.js';

function sanitizeWidgetHtml(html) {
    if (typeof html !== 'string' || html.length === 0) {
        return '';
    }
    const template = document.createElement('template');
    template.innerHTML = html;

    // Remove active/scriptable elements entirely.
    const blockedTags = [ 'script', 'iframe', 'object', 'embed', 'link', 'meta', 'style' ];
    blockedTags.forEach(tag => {
        template.content.querySelectorAll(tag).forEach(node => node.remove());
    });

    // Strip potentially dangerous attributes and javascript: URLs.
    template.content.querySelectorAll('*').forEach(node => {
        for (const attr of Array.from(node.attributes)) {
            const name = attr.name.toLowerCase();
            const value = (attr.value || '').trim().toLowerCase();
            if (name.startsWith('on') || name === 'srcdoc') {
                node.removeAttribute(attr.name);
                continue;
            }
            if ([ 'href', 'src', 'xlink:href', 'action', 'formaction' ].includes(name)
                && (value.startsWith('javascript:') || value.startsWith('data:text/html'))) {
                node.removeAttribute(attr.name);
            }
        }
    });

    return template.innerHTML;
}

// we manage the card container (ie: header and body) in the grid's shadow DOM so that we can
// handle the actions (ie: close button) and the draggable target in the header, these
// would be problematic if they existed within the card's own shadow DOM.  the actual element 
// of the card (ie: hs-table, hs-chart, etc.) is then created and appended to the card-body of the container.

export class HSGridStack extends HSGrid {
    gridstack;
    resizeObserver;
    hasUnsavedChanges = false;
    suppressChangeTracking = false;
    reloadingTabGroups = false;

    constructor() { 
        super();
        // Default to responsive sizing; only apply explicit dimensions when provided.
        const width = this.getAttribute('width');
        const height = this.getAttribute('height');
        if (width) { this.style.width = width; }
        if (height) { this.style.height = height; }
    }

    onLoad() {
        this.loadTemplate(template);
        this.loadStyles(styles);
        this.onEvent('hs-show-tab-content', opts => this.showTabContent(opts));
        this.responsiveLayoutRaf = null;
        this.resizeObserver = new ResizeObserver(() => {
            const cards = this.findChildren({ predicate: child => child instanceof HSCard, shadowOnly: false });
            for (const card of cards) {
                card.redraw();
            }
            this.scheduleResponsiveColumnUpdate();
        });
        this.resizeObserver.observe(this);
    }

    onUnload() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        this.destroy();
    }

    destroy() {
        if (this.gridstack) {
            this.gridstack.removeAll();
            this.gridstack.destroy();
            this.gridstack = null;
        }
        // clear stale DOM refs so the config can be safely passed to a new grid type
        this.cleanConfigRefs(this.config);
    }

    getResponsiveColumnCount(containerWidth = 0) {
        if (containerWidth >= 1320) { return 12; }
        if (containerWidth >= 1120) { return 10; }
        if (containerWidth >= 900) { return 8; }
        if (containerWidth >= 720) { return 6; }
        if (containerWidth >= 560) { return 4; }
        return 2;
    }

    applyResponsiveColumnsToGrid(gridRef) {
        if (!gridRef || !gridRef.el) {
            return;
        }
        const width = gridRef.el.clientWidth || gridRef.el.getBoundingClientRect().width || 0;
        const columnCount = this.getResponsiveColumnCount(width);
        Logger.debug('applying responsive columns to grid: ', { id: gridRef.opts?.id, width, columnCount });
        gridRef.column(columnCount, false);

        // Recurse into nested subgrids and apply columns using each subgrid's own width.
        for (const item of gridRef.getGridItems()) {
            const subgridEl = item.querySelector('.subgrid');
            const subgridRef = subgridEl?.gridstack;
            if (subgridRef) {
                this.applyResponsiveColumnsToGrid(subgridRef);
            }
        }
    }

    applyResponsiveColumns() {
        if (!this.gridstack || !this.isConnected) {
            return;
        }
        this.applyResponsiveColumnsToGrid(this.gridstack);
    }

    scheduleResponsiveColumnUpdate() {
        if (this.responsiveLayoutRaf) {
            cancelAnimationFrame(this.responsiveLayoutRaf);
        }
        this.responsiveLayoutRaf = requestAnimationFrame(() => {
            this.applyResponsiveColumns();
        });
    }

    setSaveButtonVisible(visible) {
        const saveButton = this.shadowRoot?.querySelector('#save-config');
        if (!saveButton) { return; }
        if (visible) {
            saveButton.classList.remove('hide');
        } else {
            saveButton.classList.add('hide');
        }
    }

    markDirty() {
        if (this.suppressChangeTracking || !this.editMode) {
            return;
        }
        this.hasUnsavedChanges = true;
        this.setSaveButtonVisible(true);
    }

    init(configOrGrids = [], cards = []) {
        let grids = configOrGrids;
        let pageCards = cards;
        if (configOrGrids && typeof configOrGrids === 'object' && !Array.isArray(configOrGrids)) {
            this.config = configOrGrids;
            grids = configOrGrids.grids || [];
            pageCards = configOrGrids.cards || [];
        } else {
            this.config = { grids, cards: pageCards };
        }

        // create our top-level gridstack container, within which we may have cards and other nested grids
        const parentNode = this.shadowRoot.querySelector("#gridstack");
        const options = { 
            id: 'main',
            draggable: { handle: '.grid-drag-target' },
            margin: 4, 
            //marginTop: 0, marginBottom: 0, marginLeft: 4, marginRight: 4,
            minRow: 1, 
            disableDrag: false,
            disableResize: false,
            children: [], 
            //cellHeight: 186 
            //staticGrid: true,
            cellHeight: 156
        };
        this.gridstack = GridStack.init(options, parentNode);
        this.config.element = this.gridstack;
        GridStack.renderCB = (el, w) => {
            el.innerHTML = sanitizeWidgetHtml(w?.content);
        };

        this.hasUnsavedChanges = false;
        this.setSaveButtonVisible(false);

        this.gridstack.on('change', (ev, items) => {
            for (const item of items) {
                if (item.id.startsWith('card')) {
                    this.redrawCard(item.id); 
                }
            }
            this.markDirty();
        });

        this.gridstack.on('resizestop', (ev, el) => {
            let node = el.gridstackNode;
            Logger.debug("grid resized event: ", ev, " node: ", node, " el: ", el);
        });

        const addGridButton = this.shadowRoot.getElementById('add-grid');
        this.onEvent('click', () => this.addGrid(this.gridstack), addGridButton);

        const saveButton = this.shadowRoot.querySelector('#save-config');
        this.onEvent('click', () => this.emit({ type: 'hs-save-config' }), saveButton);

        //const compactButton = this.shadowRoot.getElementById('compact');
        //this.onEvent('click', () => this.compact(this.gridstack), compactButton);

        //this.gridstack.float(true);
        //this.gridstack.batchUpdate(true);
        this.suppressChangeTracking = true;
        grids.forEach(config => {
            if (this.isConfigVisible(config)) {
                this.addGrid(this.gridstack, config);
            }
        });
        pageCards.forEach(config => {
            if (this.isConfigVisible(config)) {
                this.loadCard(this.gridstack, config);
            }
        });
        this.reloadTabGroups(this.shadowRoot);
        this.applyResponsiveColumns();
        this.suppressChangeTracking = false;
        //this.gridstack.batchUpdate(false);
        //this.gridstack.float(false);
    }

    /*
    compact(grid, widget) {
        Logger.debug("compacting ", grid, widget);
        if (grid && widget) {
            grid.resizeToContent(widget);
        }
    }
    */

    clear(subgridConfig = null) {
        // close all grids and cards except those which are set with "alwaysVisible"
        if (subgridConfig) {
            for (const grid of subgridConfig.grids || []) {
                if (grid.element) {
                    this.closeGrid(grid.parentGrid, grid.grid, grid.element);
                }
            }
            for (const card of subgridConfig.cards || []) {
                if (card.widget) {
                    this.closeCard(subgridConfig.grid, card.widget);
                }
            }
            return;
        }
        const widgets = this.gridstack.save(true, false);
        Logger.debug("clearing widgets: ", widgets);
        for (const widget of widgets) {
            if (widget.subGridOpts) {
                if (widget.alwaysVisible) {
                    Logger.debug("not clearing alwaysVisible grid: ", widget);
                } else {
                    Logger.debug("closing grid: ", widget);
                    widget.closeGrid();
                }
            }
        }
    }

    addGrid(parentGrid, { title, x, y, w = 6, minW = 1, minH = 1, maxW = 12, maxH = 12, grids = [], cards = [] } = {}) {
        const gridConfig = arguments[1] || {};
        if (gridConfig?.element?.isConnected) {
            return gridConfig.element;
        }
        if (gridConfig?.element && !gridConfig.element.isConnected) {
            gridConfig.element = null;
        }
        if (!parentGrid) { parentGrid = this.gridstack; }
        const id = `grid-${this.gridIdCounter++}`;  // NOTE: id is not saved/loaded, it is auto-generated when we add to ensure unique
        //Logger.debug("adding grid with original dimensions: ", { x, y, w });
        const opts = { 
            id, w, minW, minH, maxW, maxH,  // we do not specify 'h', height will be automatic based on content
            content: gridTemplate,
            sizeToContent: true,
            subGridOpts: { children: [], id, class: 'subgrid' }
        };
        if (x !== undefined) { opts.x = x; }
        if (y !== undefined) { opts.y = y; Logger.debug("y was already set to ", y); }
        Logger.debug("adding subgrid: ", gridConfig.id);
        const widget = parentGrid.addWidget(opts);
        const subwidget = widget.querySelector(".subgrid");
        subwidget.classList.add("edit-mode");
        const subgrid = subwidget.gridstack;
        const node = widget.gridstackNode;
        if (node) {
            node.alwaysVisible = gridConfig.alwaysVisible;
            node.closeGrid = this.closeGrid.bind(this, parentGrid, subgrid, widget);
            node.configRef = gridConfig;
        }
        gridConfig.element = widget;
        gridConfig.grid = subgrid;
        gridConfig.parentGrid = parentGrid;
        gridConfig.widget = widget;

        if (gridConfig.tabbar) {
            const gridHeader = widget.querySelector('.grid-header-left');
            if (gridHeader) {
                gridHeader.innerHTML = '';
                const tabbar = document.createElement('div');
                tabbar.classList.add('tabbar-container');
                for (const grid of gridConfig?.grids || []) {
                    const tab = document.createElement('div');
                    tab.classList.add('tabbar-item', 'clickable');
                    tab.textContent = grid.tabText;
                    tab.id = grid.id;
                    this.onEvent('click', () => this.reloadGridTabs(gridConfig, grid.id), tab);
                    tabbar.appendChild(tab);
                }
                gridHeader.appendChild(tabbar);
            }

            this.removeSupplementalControls(widget);

            this.reloadGridTabs(gridConfig);
            this.setChildrenEditMode(widget, this.editMode);
            return;
        }

        this.bindGridHeader(widget, { ...gridConfig, title });
        const userProviders = UserSession.get('providers') || [];
        const singleProviderUser = Array.isArray(userProviders) && userProviders.length === 1;
        if (gridConfig.id === 'providers' && singleProviderUser) {
            gridConfig.view = 'table';
        }
        widget.__activeView = gridConfig.defaultView || gridConfig.view || 'tiles';
        const hasStatsCard = (gridConfig.cards || []).some(cardConfig =>
            this.isConfigVisible(cardConfig) && cardConfig?.view === 'stats'
        );
        const controlPolicy = this.resolveSupplementalControls(gridConfig, {
            activeView: widget.__activeView,
            hasStatsCard,
            isTabbar: false,
        });
        this.bindGridSearch(widget, {
            enabled: controlPolicy.search,
            onSearch: str => {
                const cards = this.findChildren({ node: widget, predicate: child => child instanceof HSCard });
                for (const card of cards) {
                    card.search(str);
                }
            }
        });
        this.bindDateRangePicker(widget, { enabled: controlPolicy.dateRangePicker });
        this.bindViewPicker(widget, {
            enabled: controlPolicy.viewPicker,
            onViewChange: view => this.updateGridView(widget, subgrid, gridConfig, view),
            activeView: widget.__activeView
        });
        this.setStatsControlsVisibility(widget, controlPolicy.statsControlsRow, hasStatsCard);
        this.bindTimeScopes(widget, {
            timeScopes: gridConfig.timeScopes,
            onTimeScope: timeScope => this.setTimeScope({ timeScope, subgrid: widget })
        });
        const initialTimeScope = UserSession.get('timeScope') || gridConfig?.timeScopes?.[gridConfig.timeScopes.length - 1] || 'month';
        this.bindSnapshotPicker(widget, {
            enabled: controlPolicy.snapshotPicker,
            analysisWindow: initialTimeScope,
        });
        this.bindStatsColumnPresetPicker(widget, {
            enabled: controlPolicy.statsColumnPicker,
        });
        const initialTimeScopePicker = widget.querySelector('hs-time-picker');
        if (initialTimeScopePicker) {
            initialTimeScopePicker.style.display = controlPolicy.timePicker ? 'flex' : 'none';
        }

        const addGridButton = widget.querySelector('.add-subgrid');
        this.onEvent('click', () => this.addGrid(subgrid), addGridButton);
        
        const addCardButton = widget.querySelector('.add-card');
        this.onEvent('click', () => this.runWorkflow(subgrid, 'default'), addCardButton);

        /*
        const moreColButton = widget.querySelector('.more-col');
        this.onEvent('click', () => this.increaseColumns(parentGrid, subgrid, widget), moreColButton);

        const lessColButton = widget.querySelector('.less-col');
        this.onEvent('click', () => this.decreaseColumns(parentGrid, subgrid, widget), lessColButton);

        const compactButton = widget.querySelector('.compact');
        this.onEvent('click', () => this.compact(subgrid, widget), compactButton);
        */

        const closeButton = widget.querySelector('.close-grid');
        this.onEvent('click', () => this.closeGrid(parentGrid, subgrid, widget), closeButton);
        this.setChildrenEditMode(widget, this.editMode);

        grids.forEach(config => {
            if (this.isGridVisible(gridConfig, config)) {
                this.addGrid(subgrid, config);
            }
        });
        //cards.forEach(config => {
        cards.sort(this.widgetLayoutComparitor).forEach(config => {
            if (this.isConfigVisible(config) && this.matchesGridView({ ...gridConfig, view: widget.__activeView }, config)) {
                this.loadCard(subgrid, config);
            }
        });
    }

    updateGridView(widget, grid, gridConfig, view) {
        if (widget.__activeView === view) {
            return;
        }
        widget.__activeView = view;

        const gridItems = grid.getGridItems();
        for (const gridItem of gridItems) {
            const card = gridItem.querySelector('.card-body > *');
            if (card instanceof HSCard && card.config?.view && card.config.view != view) {
                this.closeCard(grid, gridItem);
            }
        }
        const activeCards = this.findChildren({ node: widget, predicate: child => child instanceof HSCard });
        const activeIds = new Set(activeCards.map(card => card.config?.id).filter(Boolean));
        for (const cardConfig of gridConfig.cards || []) {
            if (!this.isConfigVisible(cardConfig) || cardConfig?.view != view) {
                continue;
            }
            if (cardConfig.id && activeIds.has(cardConfig.id)) {
                continue;
            }
            this.loadCard(grid, cardConfig);
        }

        // Handle snapshot picker visibility based on view
        const snapshotPicker = widget.querySelector('hs-snapshot-picker');
        const timeScopePicker = widget.querySelector('hs-time-picker');
        const hasStatsCard = (gridConfig.cards || []).some(cardConfig =>
            this.isConfigVisible(cardConfig) && cardConfig?.view === 'stats'
        );
        const controlPolicy = this.resolveSupplementalControls(gridConfig, {
            activeView: view,
            hasStatsCard,
            isTabbar: false,
        });
        this.setStatsControlsVisibility(widget, controlPolicy.statsControlsRow, hasStatsCard);
        if (snapshotPicker) {
            const timeScope = UserSession.get('timeScope') || gridConfig?.timeScopes?.[gridConfig.timeScopes.length - 1] || 'month';
            this.bindSnapshotPicker(widget, {
                enabled: controlPolicy.snapshotPicker,
                analysisWindow: timeScope,
            });
            this.bindStatsColumnPresetPicker(widget, {
                enabled: controlPolicy.statsColumnPicker,
            });
        }
        if (timeScopePicker) {
            if (controlPolicy.timePicker) {
                timeScopePicker.style.display = 'flex';
            } else {
                timeScopePicker.style.display = 'none';
            }
        }
    }

    addCard(id, grid, { x, y, w = 2, h = 2, minW = 1, maxW = 12, minH = 1, maxH = 6 } = {}) {
        //Logger.debug("adding card with original dimensions: ", { x, y, w, h });
        const opts = { 
            id, w, h, minW, minH, maxW, maxH,
            content: cardTemplate 
        };
        if (x !== undefined) { opts.x = x; }
        if (y !== undefined) { opts.y = y; }
        const widget =  grid.addWidget(opts);
        //Logger.debug("after adding card, dimensions show: ", Utils.pick(opts, 'x', 'y', 'w', 'y'));
        //Logger.debug("after adding card, widget shows: ", Utils.pick(widget.gridstackNode, 'x', 'y', 'w', 'y'));
        const node = widget.gridstackNode;
        if (node) {
            node.configRef = arguments[2] || null;
        }
        return widget;
    }

    drawCardContent(grid, widget, id, config) {
        const configRef = config;
        const {
            element: _element,
            widget: _widget,
            grid: _grid,
            parentGrid: _parentGrid,
            ...serializableConfig
        } = (config || {});
        // merge/inherit existing card widget dimensions but allow new config to override
        config = {
            ...this.getWidgetLayout(widget.gridstackNode),
            ...serializableConfig
        };
        Logger.debug("updating card config: ", config);
        grid.update(widget, config);
        const node = widget.gridstackNode;
        if (node) {
            node.configRef = configRef;
        }

        // remove any previous card content (if any)
        const card_body = widget.querySelector(".card-body");
        while (card_body.hasChildNodes()) {
            card_body.removeChild(card_body.firstChild);
        }
        const card = document.createElement(config.type);
        card.setAttribute('id', id);
        card_body.appendChild(card);
        //widget.querySelector(".card-title").textContent = `${cardState.id} - ${cardState.config.title}`;

        const prevButton = widget.querySelector('#prev-button');
        prevButton.classList.add('hide');

        const applyButton = widget.querySelector('#apply-button');
        applyButton.classList.add('hide');

        const editButton = widget.querySelector('#edit-json');
        const userinfo = UserSession.get('userinfo');
        const canEditJson = hasCapability(userinfo, 'dashboard.json.edit');
        if (this.editMode && canEditJson) {
            editButton.classList.remove('hide');
            card.onEvent('click', this.editCard.bind(this, grid, widget, id, structuredClone(config)), editButton);
        } else {
            editButton.classList.add('hide');
        }

        const closeButton = widget.querySelector('#close-button');
        card.onEvent('click', this.closeCard.bind(this, grid, widget), closeButton);

        card.init(config);

        // if this is a table with search enabled (workflow mode), wire up the search input
        if (config.type === 'hs-table' && config.search?.enabled && typeof card.search === 'function') {
            const searchContainer = widget.querySelector('#search');
            if (searchContainer) {
                searchContainer.classList.remove('hide');
                const searchInput = widget.querySelector('#search-text');
                if (searchInput) {
                    card.onEvent('input', (ev) => card.search((ev.target.value ?? '').trim()), searchInput);
                }
            }
        }
        card.editMode = this.editMode;
        this.setChildrenEditMode(widget, this.editMode);
        Object.assign(configRef, config, {
            element: card,
            widget,
            grid,
        });

        // Trigger an immediate redraw on the next frame for first-pass sizing,
        // then a delayed redraw after GridStack animation settles.
        requestAnimationFrame(() => this.redrawCard(id));
        // there is an animation when adding the card which causes issues with the content
        // initial size of the container isn't well known until after animation completes
        // so, we automatically call the child's redraw() after the .3s delay for animation
        setTimeout(() => this.redrawCard(id), 300);
        return card;
    }

    /*
    toggleEditMode(enabled) {
        super.toggleEditMode(enabled);
        this.gridstack.enableResize(enabled);
        this.gridstack.enableMove(enabled);
    }
    */

    increaseColumns(parentGrid, grid, widget) {
        const columns = grid.getColumn();
        if (columns < 12) {
            grid.column(columns + 1);
            parentGrid.update(widget, { w: columns + 1 });
        }
    }

    decreaseColumns(parentGrid, grid, widget) {
        const columns = grid.getColumn();
        if (columns > 1) {
            grid.column(columns - 1);
            parentGrid.update(widget, { w: columns - 1 });
        }
    }

    editCard(grid, widget, id, config) {
        const jsonEditor = { type: "hs-editor", title: "JSON Editor", minW: 6, minH: 3, maxH: 12, maxW: 12, };
        const editor = this.drawCardContent(grid, widget, id, jsonEditor);
        editor.setContent(config);
        
        const editButton = widget.querySelector('#edit-json');
        editButton.classList.add('hide');

        const prevButton = widget.querySelector('#prev-button');
        prevButton.classList.remove('hide');
        editor.onEvent('click', this.drawCardContent.bind(this, grid, widget, id, config), prevButton);

        const applyButton = widget.querySelector('#apply-button');
        applyButton.classList.remove('hide');
        editor.onEvent('click', () => { 
            if (editor.isValid()) {
                this.drawCardContent(grid, widget, id, editor.getContent());
                this.markDirty();
            } else {
                Logger.warn("not applying invalid JSON");
            }
        }, applyButton);
    }

    configSaved() {
        this.hasUnsavedChanges = false;
        this.setSaveButtonVisible(false);
    }

    toggleEditMode(enabled) {
        super.toggleEditMode(enabled);
        this.setSaveButtonVisible(enabled && this.hasUnsavedChanges);
    }

    reloadGridTabs(gridConfig, id = null) {
        const tabbar = gridConfig?.element?.querySelector('.tabbar-container');
        if (!tabbar) { return; }
        const tabs = tabbar.querySelectorAll('.tabbar-item');
        if (tabs.length < 1) { return; }
        if (id === null) {
            const displayed = (gridConfig?.grids || []).find(g => g.displayed);
            id = displayed?.id || tabs[0].id;
        }

        // Persist visible tabbar content in config so reloadGrid() keeps exactly one child mounted.
        for (const candidate of gridConfig?.grids || []) {
            candidate.displayed = candidate.id === id;
        }

        tabs.forEach(tab => {
            if (tab.id === id) {
                tab.classList.add('selected');
                tab.classList.remove('clickable');
            } else {
                tab.classList.add('clickable');
                tab.classList.remove('selected');
            }
        });
        this.clear(gridConfig);
        const grid = gridConfig?.grids?.find(g => g.id === id);
        if (!grid) {
            Logger.warn('Could not find grid with id ' + id + ' to load for tab bar');
            return;
        }
        this.addGrid(gridConfig.grid, grid);
    }

    async reloadData() {
        await this.reloadGrid(this.config, this.gridstack);
        const components = this.findChildren({
            predicate: child => child instanceof HSComponent && !(child instanceof HSGrid) && !(child instanceof HSCard),
            shadowOnly: false,
        });
        for (const component of components) {
            if (typeof component.reloadData === 'function') {
                component.reloadData();
            }
        }
        this.reloadTabGroups(this.shadowRoot);
    }

    reloadTabGroups(node = this.shadowRoot) {
        if (this.reloadingTabGroups) {
            return;
        }
        this.reloadingTabGroups = true;
        const cards = this.findChildren({ node, predicate: child => child instanceof HSComponent && child.config.tabGroup });
        Logger.debug('reloadTabGroups scope: ', {
            node_tag: node?.tagName,
            node_id: node?.id,
            node_config_id: node?.config?.id,
            cards_found: cards.length,
            card_ids: cards.map(card => card?.config?.id),
        });
        const tabGroups = new Set();
        for (const card of cards || []) {
            if (card.config?.tabGroup) {
                tabGroups.add(card.config.tabGroup);
            }
        }
        Logger.debug('reloadTabGroups tabGroups: ', Array.from(tabGroups));
        try {
            for (const tabGroup of tabGroups) {
                this.showTabContent({ tabGroup });
            }
        } finally {
            this.reloadingTabGroups = false;
        }
    }

    async reloadGrid(config, parentGrid) {
        if (!config) {
            return;
        }
        for (const grid of config.grids || []) {
            const gridMounted = !!(grid.element && grid.element.isConnected && grid.grid);
            if (!gridMounted && (grid.element || grid.grid || grid.parentGrid || grid.widget)) {
                delete grid.element;
                delete grid.grid;
                delete grid.parentGrid;
                delete grid.widget;
            }
            if (this.isGridVisible(config, grid)) {
                if (!gridMounted) {
                    this.addGrid(parentGrid, grid);
                } else {
                    await this.reloadGrid(grid, grid.grid);
                }
            } else if (gridMounted) {
                this.closeGrid(grid.parentGrid, grid.grid, grid.element);
            }
        }

        for (const card of config.cards || []) {
            const cardMounted = !!(card.widget && card.widget.isConnected && card.element);
            if (!cardMounted && (card.widget || card.element || card.grid)) {
                delete card.widget;
                delete card.element;
                delete card.grid;
            }
            const visible = this.isConfigVisible(card) && this.matchesGridView(config, card);
            if (visible) {
                if (!cardMounted) {
                    this.loadCard(parentGrid, card);
                } else if (card.element && typeof card.element.reloadData === 'function') {
                    card.element.reloadData();
                }
            } else if (cardMounted) {
                this.closeCard(parentGrid, card.widget);
            }
        }
    }

    findGridConfigById(id, config = this.config) {
        if (!config) { return null; }
        for (const grid of config.grids || []) {
            if (grid.id === id) {
                return grid;
            }
            const nested = this.findGridConfigById(id, grid);
            if (nested) {
                return nested;
            }
        }
        return null;
    }

    showTabContent({ id, tabGroup, container = null, content = null } = {}) {
        const requestedId = id;
        tabGroup = this.resolveTabGroup(tabGroup, id);
        if (!tabGroup) {
            Logger.warn('Cannot show tab content, missing tabGroup', { id, container, content });
            return;
        }
        let tab = null;
        if (id) {
            tab = this.findTabById(tabGroup, id);
        } else {
            const rememberedId = this.getRememberedTabId(tabGroup);
            tab = rememberedId ? this.findTabById(tabGroup, rememberedId) : null;
            if (!tab) {
                tab = this.findActiveTab(tabGroup) || this.pickDefaultTab(tabGroup);
            }
            id = tab?.config?.id;
        }
        if (tab && tab.selected && requestedId) {
            return;
        }
        if (!tab) {
            Logger.warn('no tab found for tabgroup: ', { id, tabGroup, container, content });
            return;
        }

        if (!container && tab.config?.onclick?.action == 'showTabContent') {
            container = tab.config.onclick.container;
        }
        if (!content && tab.config?.onclick?.action == 'showTabContent') {
            content = tab.config.onclick.content;
        }
        if (!content) {
            content = tab.config.id;
        }
        if (!content) {
            Logger.warn('no content id for tabGroup: ', tabGroup);
            return;
        }

        let containerConfig = null;
        if (container) {
            containerConfig = this.findGridConfigById(container);
            if (!containerConfig) {
                Logger.warn("can't find tab container: ", container);
                return;
            }
        }

        const parentConfig = containerConfig || this.config;
        const parentGrid = containerConfig?.grid || this.gridstack;
        const grid = parentConfig?.grids?.find(g => g.id === content);
        if (!grid) {
            Logger.warn('Could not find content with id ' + content + ' to load for tab group');
            return;
        }

        // Persist active tab content visibility so reloadGrid() does not close selected content.
        for (const candidate of parentConfig?.grids || []) {
            if (this.isTabControlledGrid(parentConfig, candidate)) {
                candidate.displayed = candidate.id === content;
            }
        }
        const contentMounted = !!(grid.element && grid.element.isConnected);
        if (tab.selected && contentMounted) {
            this.rememberTabSelection(tabGroup, id);
            return;
        }

        this.clear(containerConfig || null);
        this.unselectAllTabs(tabGroup);
        this.selectTab(id, tabGroup);
        this.rememberTabSelection(tabGroup, id);
        this.addGrid(parentGrid, grid);
    }

    getWidgetLayout(widget) {
        return Utils.pickIfDefined(widget, 'x', 'y', 'w', 'h');
    }

    saveWidgets(widgets) {
        const cards = [];
        const grids = [];
        for (const widget of widgets) {
            if (widget.subGridOpts) {
                // NOTE: we use a ...rest operator trick to remove old config items which are no longer used...
                const { width, height, minWidth, minHeight, maxWidth, maxHeight, ...config } = { 
                    ...this.saveWidgets(widget.subGridOpts?.children ?? []),
                    ...this.getWidgetLayout(widget)
                };
                Logger.debug("saving grid: ", { id: widget.id, ...config });
                grids.push(config);
            } else {
                const card = this.shadowRoot.getElementById(widget.id);
                if (card) {
                    //Logger.debug("saving card: ", { id: widget.id, widget, layout: this.getWidgetLayout(widget), config: card.saveConfig() });
                    const { width, height, minWidth, minHeight, maxWidth, maxHeight, ...config } = { 
                        ...card.saveConfig(),
                        ...this.getWidgetLayout(widget)
                    };
                    Logger.debug("saving card: ", { id: widget.id, ...config });
                    cards.push(config);
                } else {
                    Logger.warn("not saving unknown widget: ", widget);
                }
            }
        }
        return { cards, grids };
    }

    saveConfig() {
        // find all subgrids and card components in the DOM stack
        const cards = this.findChildren({ predicate: child => child instanceof HSComponent });
        // TODO: save these top-level cards in the gridstack...
        //
        const widgets = this.gridstack.save(false, false);
        return this.saveWidgets(widgets);
    }

    closeGrid(parentGrid, grid, widget) {
        // TODO: should probably do other cleanup here to avoid memory leaks
        const config = widget?.gridstackNode?.configRef;
        if (config) {
            config.element = null;
            config.grid = null;
            config.parentGrid = null;
            config.widget = null;
        }
        grid.removeAll();
        grid.destroy();
        parentGrid.removeWidget(widget);
        this.markDirty();
    }

    closeCard(grid, widget) {
        // TODO: should probably do other cleanup here to avoid memory leaks
        const config = widget?.gridstackNode?.configRef;
        if (config) {
            config.element = null;
            config.widget = null;
            config.grid = null;
        }
        grid.removeWidget(widget);
        this.markDirty();
    }
}
customElements.define('hs-gridstack', HSGridStack);
